/**
 * @file startRgsMockWorker.ts
 * @role Starts the MSW service worker that impersonates the RGS in the browser. Since the fake
 *       server is now the ONLY source of mock outcomes, this runs for both fake modes: `mock`
 *       (zero latency, the dev default) and `msw` (the configured latency). It is what lets
 *       HttpRgsClient — the only IRgsClient — run for real before the deployed server ships
 *       CORS (docs/RGS_SERVER_REQUIREMENTS.md A1).
 * @owns The worker lifecycle only.
 * @reads GameEnvironment (never import.meta.env directly — that boundary is GameEnvironment's).
 * @neverDoes Run outside dev/mock-preview (Invariant 6). Decide game outcomes.
 * @invariants 6 (fakes are dev/test only).
 * @see src/mocks/rgsHandlers.ts, src/core/backend/RgsMode.ts
 */
import { IS_DEV_BUILD, IS_MOCK_PREVIEW_BUILD, readGameEnvironment } from '@app/GameEnvironment';
import gameConfig from '@config/game.config.json';
import { MockScenarioSchema } from '@core/config/ConfigSchema';
import { resolveRgsMode } from '@core/backend/RgsMode';

/**
 * Start the fake RGS if this build asked for it. Returns once the worker is intercepting, so
 * boot can await it and no early request escapes to the network.
 */
export async function startRgsMockWorker(): Promise<string | null> {
  // Production never fakes anything, and this must return quietly rather than raise: a throw
  // here happens before there is a canvas to report it on. RgsClientFactory raises the same
  // misconfiguration a moment later, loudly, with the boot shell up to show it.
  if (!IS_DEV_BUILD && !IS_MOCK_PREVIEW_BUILD) return null;

  const environment = readGameEnvironment();
  const mode = resolveRgsMode(environment, gameConfig);
  if (mode === 'live') return null;

  const [{ setupWorker }, { createRgsHandlers }] = await Promise.all([
    import('msw/browser'),
    import('./rgsHandlers'),
  ]);

  const baseUrl = environment.rgsBaseUrl || gameConfig.rgs.baseUrl;
  // `mock` answers instantly; `msw` adds the configured latency so the states that only exist
  // while a request is in flight — locked HUD, watchdog, minimum spin duration — are exercised.
  const responseDelayMs = mode === 'msw' ? gameConfig.mockRgs.responseDelayMs : 0;
  // VITE_MOCK_SCENARIO still selects a specific outcome; without it the handlers cycle their
  // fixtures.
  const scenario = MockScenarioSchema.safeParse(environment.mockScenario);
  const handlers = createRgsHandlers(
    baseUrl,
    responseDelayMs,
    scenario.success ? scenario.data : undefined,
  );
  await setupWorker(...handlers).start({
    quiet: true,
    // Anything this fake RGS does not define should reach the network exactly as it would in
    // production, rather than being silently swallowed.
    onUnhandledRequest: 'bypass',
  });
  return baseUrl;
}
